import { aabbFromQuad } from '@/features/defect-drawer/utils/projectModuleRingToIrQuad';

export const SNAP_SCORE_THRESHOLD = 0.55;
export const DEFAULT_PAD_RATIO = 0.25;

function clamp(n, min, max) {
  return Math.min(max, Math.max(min, n));
}

function boxCenter(box) {
  return { x: box.x + box.width / 2, y: box.y + box.height / 2 };
}

function similarity(a, b) {
  const denom = Math.max(Math.abs(a), Math.abs(b), 1e-6);
  return 1 - Math.min(1, Math.abs(a - b) / denom);
}

export function scoreSnapCandidate(projectedAabb, candidateAabb, searchDiag) {
  if (!projectedAabb || !candidateAabb) {
    return 0;
  }
  const pc = boxCenter(projectedAabb);
  const cc = boxCenter(candidateAabb);
  const dist = Math.hypot(pc.x - cc.x, pc.y - cc.y);
  const diag = Number(searchDiag) > 0 ? Number(searchDiag) : 1;
  const centerScore = clamp(1 - dist / diag, 0, 1);
  const areaScore = similarity(
    projectedAabb.width * projectedAabb.height,
    candidateAabb.width * candidateAabb.height
  );
  const aspectScore = similarity(
    projectedAabb.width / projectedAabb.height,
    candidateAabb.width / candidateAabb.height
  );
  return 0.5 * centerScore + 0.3 * areaScore + 0.2 * aspectScore;
}

export function shouldApplyCoveringSnap({ userMovedRect, snapResult }) {
  return !userMovedRect && snapResult?.source === 'snap';
}

function isQuad(quad) {
  return Array.isArray(quad) && quad.length >= 4;
}

/** 스냅 입력은 항상 원본 투영. 스냅 결과로 덮어쓰면 클릭마다 박스가 커진다. */
export function resolveCoveringSnapInput({ projectedQuad, overlayQuad }) {
  if (isQuad(projectedQuad)) {
    return projectedQuad;
  }
  if (isQuad(overlayQuad)) {
    return overlayQuad;
  }
  return null;
}

/** overlay PATCH 같은 같은 장 갱신은 이미지 리로드로 취급하지 않는다. */
export function shouldHandleThermalImageChange(prev, next) {
  if (!next) {
    return false;
  }
  if (prev == null) {
    return true;
  }
  const prevId = prev.id;
  const nextId = next.id;
  if (prevId == null || nextId == null) {
    return true;
  }
  return Number(prevId) !== Number(nextId);
}

function grayAt(imageData, x, y) {
  const i = (y * imageData.width + x) * 4;
  const d = imageData.data;
  return 0.299 * d[i] + 0.587 * d[i + 1] + 0.114 * d[i + 2];
}

function darkPixelAabb(imageData, windowBox) {
  const x0 = Math.floor(clamp(windowBox.x, 0, imageData.width - 1));
  const y0 = Math.floor(clamp(windowBox.y, 0, imageData.height - 1));
  const x1 = Math.ceil(clamp(windowBox.x + windowBox.width, 0, imageData.width));
  const y1 = Math.ceil(clamp(windowBox.y + windowBox.height, 0, imageData.height));
  let sum = 0;
  let count = 0;
  for (let y = y0; y < y1; y += 1) {
    for (let x = x0; x < x1; x += 1) {
      sum += grayAt(imageData, x, y);
      count += 1;
    }
  }
  if (count === 0) {
    return null;
  }
  const mean = sum / count;
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  let darkCount = 0;
  for (let y = y0; y < y1; y += 1) {
    for (let x = x0; x < x1; x += 1) {
      if (grayAt(imageData, x, y) < mean - 12) {
        darkCount += 1;
        if (x < minX) minX = x;
        if (y < minY) minY = y;
        if (x > maxX) maxX = x;
        if (y > maxY) maxY = y;
      }
    }
  }
  if (darkCount < 16) {
    return null;
  }
  return aabbFromQuad([
    [minX, minY],
    [maxX, minY],
    [maxX, maxY],
    [minX, maxY]
  ]);
}

export function snapIrQuadToNearbyModule({
  imageData,
  projectedQuad,
  padRatio = DEFAULT_PAD_RATIO
}) {
  const projectedAabb = aabbFromQuad(projectedQuad);
  const fallback = {
    quad: projectedQuad,
    aabb: projectedAabb,
    source: 'project'
  };
  if (!imageData?.data || !projectedAabb) {
    return fallback;
  }
  const pad = Math.max(projectedAabb.width, projectedAabb.height) * padRatio;
  const windowBox = {
    x: projectedAabb.x - pad,
    y: projectedAabb.y - pad,
    width: projectedAabb.width + pad * 2,
    height: projectedAabb.height + pad * 2
  };
  const candidate = darkPixelAabb(imageData, windowBox);
  if (!candidate) {
    return fallback;
  }
  const searchDiag = Math.hypot(windowBox.width, windowBox.height);
  const score = scoreSnapCandidate(projectedAabb, candidate, searchDiag);
  if (score < SNAP_SCORE_THRESHOLD) {
    return fallback;
  }
  return {
    quad: [
      [candidate.x, candidate.y],
      [candidate.x + candidate.width, candidate.y],
      [candidate.x + candidate.width, candidate.y + candidate.height],
      [candidate.x, candidate.y + candidate.height]
    ],
    aabb: candidate,
    source: 'snap'
  };
}
